/**
 * Die Pakete – eine Wahrheit für Startseite, Preisseite und Server.
 *
 * Alle drei lesen von hier. Der Grund ist der übliche: ein Preis, der an zwei
 * Stellen steht, steht früher oder später zweimal verschieden da, und dann
 * ist es der Kunde, der es zuerst merkt.
 *
 * Dass diese Datei im gemeinsamen Teil liegt und nicht im Browser, hat einen
 * zweiten Grund: seit es eine Registrierung gibt, kommt die Paketwahl aus dem
 * Formular eines Fremden. Der Server muss sie prüfen können – und zwar gegen
 * dieselbe Liste, die der Besucher auf der Preisseite gesehen hat.
 *
 * Was hier steht, ist eine Geschäftsentscheidung und keine Technik. Wer die
 * Beträge ändert, ändert nur diese Datei.
 *
 * Alle Beträge in Rappen pro Monat, wie überall in Raporto. Angezeigt wird in
 * Franken; gerechnet wird nie in Franken, sonst kommen die Rundungsfehler.
 */

export interface Paket {
  /** Kurzname, dient auch als Anker auf der Preisseite. */
  id: 'starter' | 'pro' | 'team'
  name: string
  fuerWen: string
  preisRp: number
  /*
    Benutzerzahl und Speicher als ZAHLEN, nicht als Satz.

    Sie standen bis eben nur in den Merkmalstexten unten – «Bis 20 Benutzer»,
    «200 GB für Belege und Fotos» – und ein zweites Mal, mit anderen Werten,
    in der Vergleichstabelle der Preisseite und ein drittes Mal auf der
    Startseite. Drei Orte, drei Wahrheiten: die Startseite versprach 500 GB
    und unbegrenzte Benutzer, während die Preisseite 200 GB und zwanzig
    nannte. Wer das nebeneinander sieht, glaubt keiner der beiden Zahlen.

    Jetzt steht die Zahl einmal hier, und alle drei Flächen lesen sie von
    hier. Die Merkmalstexte werden daraus gebaut statt danebengeschrieben.
  */
  /** Wie viele Personen sich anmelden dürfen. */
  benutzer: number
  /** Speicher für Belege und Fotos, in Gigabyte. */
  speicherGb: number
  /** Das eine Merkmal, das dieses Paket vom kleineren unterscheidet. */
  kern: string
  merkmale: string[]
  /** Das mittlere Paket trägt die Empfehlung. */
  empfohlen?: boolean
}

export const PAKETE: Paket[] = [
  {
    id: 'starter',
    name: 'Basic',
    fuerWen: 'Für Einzelunternehmer',
    preisRp: 1990,
    benutzer: 1,
    speicherGb: 1,
    kern: '1 Benutzer',
    merkmale: [
      'Rechnungen und Offerten ohne Limite',
      'Schweizer QR-Rechnung',
      'Kunden, Baustellen, Leistungskatalog',
      'Powered by Raporto auf Belegen'
    ]
  },
  {
    id: 'pro',
    name: 'Pro',
    fuerWen: 'Für wachsende Betriebe',
    preisRp: 4990,
    benutzer: 5,
    speicherGb: 5,
    kern: 'Bis 5 Benutzer',
    merkmale: [
      'Alles aus Basic',
      'Rapporte am Handy, auch ohne Netz',
      'Zahlungen und offene Posten',
      'Export für den Treuhänder',
      'Powered by Raporto auf Belegen'
    ],
    empfohlen: true
  },
  {
    id: 'team',
    name: 'Premium',
    fuerWen: 'Für etablierte Unternehmen',
    preisRp: 9990,
    benutzer: 10,
    speicherGb: 10,
    kern: 'Bis 10 Benutzer',
    /*
      Der Plattformhinweis steht auch hier im Paket – und daneben, dass er
      sich bei Premium abschalten lässt.

      Beide Zeilen gehören auf die Preisseite und nicht nur in die AGB: Wer
      erst nach dem Kauf erfährt, dass auf seiner Rechnung ein fremder Name
      steht, hat den Namen nicht gekauft, sondern hingenommen.
    */
    merkmale: [
      'Alles aus Pro',
      'Mehrere Baustellenteams parallel',
      'Powered by Raporto auf Belegen',
      'White-Label-Add-on: Hinweis entfernen für 10.00/Monat'
    ]
  }
]

/** «1 Benutzer» / «Bis 5 Benutzer» – gebaut, nicht danebengeschrieben. */
export function benutzerText(p: Paket): string {
  return p.benutzer === 1 ? '1 Benutzer' : `Bis ${p.benutzer} Benutzer`
}

/** «5 GB für Belege und Fotos». */
export function speicherText(p: Paket): string {
  return `${p.speicherGb} GB für Belege und Fotos`
}

/**
 * Die Merkmalsliste, wie sie auf der Karte steht.
 *
 * Benutzer zuoberst, Speicher zuunterst – das sind die beiden Zahlen, die
 * jemand vergleicht, und sie stehen deshalb an denselben Stellen wie auf der
 * Karte daneben.
 */
export function merkmaleMitZahlen(p: Paket): string[] {
  return [benutzerText(p), ...p.merkmale, speicherText(p)]
}

/** «19.90» – ohne Währung, die steht daneben. */
export function preisText(rp: number): string {
  return (rp / 100).toFixed(2)
}

/**
 * Wie die Preise zu verstehen sind – an genau einer Stelle.
 *
 * «exkl. MWST» ist bei Schweizer Geschäftssoftware die Konvention: der Käufer
 * ist ein Betrieb und zieht die Vorsteuer ab, für ihn zählt der Nettopreis.
 * bexio und die übrigen weisen ebenso aus, und ein Preis ohne Angabe wird in
 * diesem Markt automatisch als Nettopreis gelesen – nur eben ohne dass es
 * dasteht.
 *
 * Der zweite, härtere Grund: wer nicht mehrwertsteuerpflichtig ist, darf auch
 * keine ausweisen. «inkl. MWST» wäre deshalb nicht bloss ungewohnt, sondern
 * falsch, solange die Pflicht nicht besteht.
 *
 * Steht der Hinweis an einer Stelle, ändert man ihn an einer Stelle – und er
 * fehlt nie auf einer Seite, die jemand später dazubaut.
 */
export const STEUERHINWEIS = 'exkl. MWST'

/** Die gültigen Paketkennungen – die Liste, gegen die der Server prüft. */
export const PAKET_IDS = PAKETE.map((p) => p.id)

/**
 * Ein Paket zu seiner Kennung, oder nichts.
 *
 * Der Server nimmt bei der Registrierung eine Kennung aus dem Browser
 * entgegen. Sie hier nachzuschlagen statt zu glauben ist der Unterschied
 * zwischen «Preis kommt aus der Preisliste» und «Preis kommt aus dem
 * Formular» – und das zweite ist der Fehler, mit dem sich jemand ein
 * Team-Paket für 19.90 bestellt.
 */
export function paketVon(id: string): Paket | undefined {
  return PAKETE.find((p) => p.id === id)
}

/**
 * Wie viele Benutzer ein Paket zulässt – und warum das hier nicht mehr steht.
 *
 * Bis Paket 10 gab es an dieser Stelle eine zweite Tabelle `BENUTZER_GRENZE`
 * mit `{ starter: 1, pro: 5, team: 20 }`. Sie widersprach der Preisliste
 * darüber, in der `team` auf 10 steht – und 10 ist die Zahl, die der Kunde
 * auf der Preisseite und auf der Startseite liest, weil beide `benutzerText()`
 * aus `PAKETE` bauen.
 *
 * Benutzt wurde die zweite Tabelle nirgends. Sie war eine tote Zahl, die beim
 * ersten Scharfstellen einer Grenze zur lebendigen geworden wäre – und dann
 * hätte ein Premium-Kunde plötzlich 20 Plätze gehabt, obwohl ihm 10 verkauft
 * wurden, oder umgekehrt, je nachdem, welche Stelle zuerst gelesen hätte.
 *
 * Es gibt jetzt eine Zahl: `Paket.benutzer`. Wer die Grenze braucht, nimmt
 * `grenzenVon(id)` unten oder liest das Paket selbst.
 */

/** Die beiden Grenzen eines Pakets, an einer Stelle abholbar. */
export interface Paketgrenzen {
  benutzer: number
  speicherGb: number
}

/**
 * Die Grenzen zu einer Paketkennung – oder nichts.
 *
 * Gibt bewusst `undefined` zurück statt einer Vorgabe. Wer keine Kennung hat,
 * hat kein Paket, und was dann gilt, ist eine Entscheidung des Aufrufers und
 * keine, die hier stillschweigend getroffen wird. Der Speicherpfad etwa nimmt
 * in diesem Fall die engste Grenze, und das steht dort ausdrücklich da.
 */
export function grenzenVon(id: string | null): Paketgrenzen | undefined {
  const p = id === null ? undefined : PAKETE.find((x) => x.id === id)
  return p ? { benutzer: p.benutzer, speicherGb: p.speicherGb } : undefined
}

/**
 * Wie lange die Testphase dauert.
 *
 * DREISSIG Tage. Ein Wert, eine Datei – und seit dem letzten Durchgang auch
 * wirklich nur noch einer: Auf der Startseite, der Preisseite, im Fuss, in der
 * Zahlungszusage, auf dem Bildschirm nach der Kasse und in der Willkommensmail
 * stand die Zahl getippt da. Ein Betrieb las «30 Tage testen» und bekam eine
 * andere Frist; das ist keine Kleinigkeit, sondern eine Zusage, die nicht
 * gehalten wird.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Und warum hier kurz SIEBEN stand
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Weil der Entwickler die beiden Fristen des Ausstiegs verwechselt hat. Sieben
 * Tage sind die ZAHLUNGSNACHFRIST nach einer fehlgeschlagenen Abbuchung
 * (`TEST_ARCHIV_TAGE` in config.ts), danach folgen sieben Tage Lesemodus bis
 * zur vorgesehenen Löschung an Tag vierzehn. Die kostenlose Testphase davor
 * dauert dreissig Tage und hat mit diesen Fristen nichts zu tun.
 *
 * Die beiden stehen deshalb in verschiedenen Dateien und tragen verschiedene
 * Namen: hier das Angebot, dort der Ausstieg.
 *
 * Die Zahl wirkt ausschliesslich auf NEUE Testphasen – auf die Stripe-Sitzung
 * beim Bestellen und auf `trial_ends_at` bei der Betriebsanlage. Was bereits
 * läuft, hat sein Enddatum in der Datenbank und bleibt unberührt.
 *
 * Die Allgemeinen Geschäftsbedingungen in apps/web/src/recht.ts nennen
 * dieselbe Dauer, aber ausgeschrieben und mit Fassungsdatum: Ein Vertragstext
 * ändert sich durch einen Beschluss und nicht durch eine Codezeile. Dass die
 * beiden Zahlen nicht auseinanderlaufen, prüft tests/rechtstexte.test.ts –
 * wer hier etwas ändert, sieht dort rot und braucht eine neue Fassung.
 */
export const TESTPHASE_TAGE = 30
